import { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  withSequence,
  Easing,
} from 'react-native-reanimated';
import { Sparkles, X } from 'lucide-react-native';
import { BetaLoginModal } from './BetaLoginModal';
import { WaitlistModal } from './WaitlistModal';

const BUBBLE_COLORS = {
  dark: {
    teal: '#4fd1c5',
    tealDark: '#0d9488',
    bgDeep: '#0a1628',
    white: '#FFFFFF',
    surface: 'rgba(255,255,255,0.08)',
    border: 'rgba(255,255,255,0.12)',
    fabIcon: '#0a1628',
  },
  light: {
    teal: '#0d7d71',
    tealDark: '#0a6b61',
    bgDeep: '#ffffff',
    white: '#1d1d1f',
    surface: 'rgba(0,0,0,0.04)',
    border: 'rgba(0,0,0,0.12)',
    fabIcon: '#ffffff',
  },
};

/**
 * Floating action button for the beta landing page.
 * Opens a mini-menu with "Join Waitlist" and "Sign In" options.
 */
interface BetaBubbleButtonProps {
  colorScheme?: 'dark' | 'light';
}

export function BetaBubbleButton({ colorScheme = 'dark' }: BetaBubbleButtonProps) {
  const C = BUBBLE_COLORS[colorScheme];
  const [expanded, setExpanded] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);

  // Pulse animation for the main button
  const pulse = useSharedValue(1);

  useState(() => {
    pulse.value = withRepeat(
      withSequence(
        withTiming(1.08, { duration: 1200, easing: Easing.inOut(Easing.ease) }),
        withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) }),
      ),
      -1,
      true,
    );
  });

  const pulseStyle = useAnimatedStyle(() => ({
    transform: [{ scale: expanded ? 1 : pulse.value }],
  }));

  const handleWaitlist = () => {
    setExpanded(false);
    setShowWaitlist(true);
  };

  const handleLogin = () => {
    setExpanded(false);
    setShowLogin(true);
  };

  return (
    <>
      {/* Backdrop when expanded */}
      {expanded && (
        <Animated.View
          entering={FadeIn.duration(150)}
          exiting={FadeOut.duration(150)}
          style={{
            position: 'fixed' as any,
            bottom: 0, right: 0, left: 0, top: 0,
            zIndex: 998,
          }}
        >
          <Pressable style={{ flex: 1 }} onPress={() => setExpanded(false)} />
        </Animated.View>
      )}

      {/* Menu items */}
      {expanded && (
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(150)}
          style={{
            position: 'fixed' as any,
            bottom: 100,
            right: 24,
            zIndex: 999,
            gap: 10,
            alignItems: 'flex-end',
          }}
        >
          <Pressable
            onPress={handleWaitlist}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              backgroundColor: C.bgDeep,
              borderRadius: 14,
              paddingVertical: 12,
              paddingHorizontal: 18,
              borderWidth: 1,
              borderColor: C.teal,
            }}
          >
            <Text style={{ color: C.teal, fontSize: 14, fontWeight: '600' }}>Join Waitlist</Text>
          </Pressable>

          <Pressable
            onPress={handleLogin}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              backgroundColor: C.bgDeep,
              borderRadius: 14,
              paddingVertical: 12,
              paddingHorizontal: 18,
              borderWidth: 1,
              borderColor: C.border,
            }}
          >
            <Text style={{ color: C.white, fontSize: 14, fontWeight: '600' }}>Sign In</Text>
          </Pressable>
        </Animated.View>
      )}

      {/* Main FAB */}
      <Animated.View
        style={[
          pulseStyle,
          {
            position: 'fixed' as any,
            bottom: 28,
            right: 24,
            zIndex: 1000,
          },
        ]}
      >
        <Pressable
          onPress={() => setExpanded(!expanded)}
          style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: expanded ? C.tealDark : C.teal,
            alignItems: 'center',
            justifyContent: 'center',
            // Shadow
            shadowColor: C.teal,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.35,
            shadowRadius: 12,
            elevation: 8,
          }}
        >
          {expanded ? (
            <X size={22} color={C.white} />
          ) : (
            <Sparkles size={22} color={C.fabIcon} />
          )}
        </Pressable>
      </Animated.View>

      {/* Modals */}
      <BetaLoginModal visible={showLogin} onClose={() => setShowLogin(false)} colorScheme={colorScheme} />
      <WaitlistModal visible={showWaitlist} onClose={() => setShowWaitlist(false)} colorScheme={colorScheme} />
    </>
  );
}
